import { useState, useEffect } from "react";
import { auth } from "../../config/firebase";
import PropTypes from 'prop-types';
import axios from 'axios';
// Import all avatar SVGs
import Avatar1 from "../../assets/avatars/Boy_1.svg";
import Avatar2 from "../../assets/avatars/Boy_2.svg";
import Avatar3 from "../../assets/avatars/Boy_3.svg";
import Avatar4 from "../../assets/avatars/Boy_4.svg";
import Avatar5 from "../../assets/avatars/Boy_5.svg";
import Avatar6 from "../../assets/avatars/Boy_6.svg";
import Avatar7 from "../../assets/avatars/Girl_1.svg";
import Avatar8 from "../../assets/avatars/Girl_2.svg";
import Avatar9 from "../../assets/avatars/Girl_3.svg";
import Avatar10 from "../../assets/avatars/Girl_4.svg";
import Avatar11 from "../../assets/avatars/Girl_5.svg";
import Avatar12 from "../../assets/avatars/Girl_6.svg";

// Export avatars for reuse in other components
export const avatars = [
    { id: "avatar1", src: Avatar1 },
    { id: "avatar2", src: Avatar2 },
    { id: "avatar3", src: Avatar3 },
    { id: "avatar4", src: Avatar4 },
    { id: "avatar5", src: Avatar5 },
    { id: "avatar6", src: Avatar6 },
    { id: "avatar7", src: Avatar7 },
    { id: "avatar8", src: Avatar8 },
    { id: "avatar9", src: Avatar9 },
    { id: "avatar10", src: Avatar10 },
    { id: "avatar11", src: Avatar11 },
    { id: "avatar12", src: Avatar12 },
];

const ProfileAvatar = ({ onSelect, initialAvatar }) => {
    const [selectedAvatar, setSelectedAvatar] = useState(initialAvatar || "");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [saveStatus, setSaveStatus] = useState(null);
    const userId = auth.currentUser?.uid;

    useEffect(() => {
        const fetchUserAvatar = async () => {
            if (!userId) {
                setError("No authenticated user found");
                setLoading(false);
                return;
            }

            try {
                const response = await axios.get(`https://api.frazza.online/user/${userId}/avatar`);

                if (response.data.success) {
                    // Only update if we don't have an initial avatar or if it's different
                    const fetchedAvatar = response.data.avatar || "";
                    if (!initialAvatar || fetchedAvatar !== initialAvatar) {
                        setSelectedAvatar(fetchedAvatar);
                    }
                } else {
                    // If no avatar found, create an empty one
                    await axios.put(`https://api.frazza.online/user/${userId}/avatar`, { avatarId: "" });
                    setSelectedAvatar("");
                }
            } catch (error) {
                console.error("Error fetching user avatar:", error);
                setError(error.response?.data?.error || error.message);
            } finally {
                setLoading(false);
            }
        };

        fetchUserAvatar();
    }, [userId, initialAvatar]);

    // Save selected avatar
    const saveAvatar = async (avatarId) => {
        if (!userId) {
            setError("No authenticated user found");
            return;
        }

        try {
            setSaveStatus("saving");
            setSelectedAvatar(avatarId);

            const response = await axios.put(`https://api.frazza.online/user/${userId}/avatar`, { avatarId });

            if (response.data.success) {
                setSaveStatus("success");

                // Call the onSelect prop if provided
                if (onSelect) {
                    onSelect(avatarId);
                }
            } else {
                throw new Error(response.data.error || "Failed to update avatar");
            }
        } catch (error) {
            console.error("Error updating avatar:", error);
            setError(error.response?.data?.error || error.message);
            setSaveStatus("error");
        }
    };

    if (loading) return <div className="loading-state">Loading avatars...</div>;
    if (error) return <div className="error-state">Error: {error}</div>;
    if (!userId) return <div className="signin-required">Please sign in to select an avatar</div>;

    return (
        <div className="avatar-selector">
            <h3 className="avatar-title">Choose your profile picture</h3>

            {saveStatus === "success" && (
                <div className="save-status success">Avatar saved successfully!</div>
            )}
            {saveStatus === "error" && (
                <div className="save-status error">Failed to save avatar. Please try again.</div>
            )}

            <div className="avatar-grid">
                {avatars.map((avatar) => (
                    <div
                        key={avatar.id}
                        className={`avatar-option ${selectedAvatar === avatar.id ? "selected" : ""}`}
                        onClick={() => saveAvatar(avatar.id)}
                    >
                        <img src={avatar.src} alt={`Avatar ${avatar.id}`} />
                    </div>
                ))}
            </div>
        </div>
    );
};

ProfileAvatar.propTypes = {
    onSelect: PropTypes.func,
    initialAvatar: PropTypes.string
};

export default ProfileAvatar;
